import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

import { useModalStore } from '@tix/modal'

import { setDocumentTitle } from '@/helpers/documentTitle'
import { getEnv } from '@/helpers/environment'

import { errorTracker } from '@/services/errorTracking'

import { routes } from '@/configuration/routes'

// When there is a seller slug, we want to use it as the base for the router, so that all declared routes and navigation are relative to `/<seller>/`.
// That means once the app loads with a seller slug, it cannot navigate client-side to paths without seller slug, and vice-versa.
const sellerSlug = getEnv().seller?.slug
// We don't want seller slug as base for Vitest/Storybook only as those frameworks control the url differently. We still want seller slug for playwright e2e, hence why we can't use isTestEnvironment().
const isVitestOrStorybook =
  import.meta.env.VITEST === 'true' || import.meta.env.STORYBOOK === 'true' || import.meta.env.MODE === 'test'
const historyBase = sellerSlug && !isVitestOrStorybook ? `/${sellerSlug}/` : '/'

const router = createRouter({
  history: createWebHistory(historyBase),
  routes: routes as RouteRecordRaw[],
})

export type RouteTarget = {
  name: string
  params?: Record<string, string>
  query?: Record<string, string>
}

interface RouteMetadata {
  title: string
}
// typed Metadata for routes
declare module 'vue-router' {
  interface RouteMeta extends RouteMetadata {}
}

export type RouteDefinition = Omit<RouteRecordRaw, 'meta' | 'children'> & {
  meta?: Partial<RouteMetadata> // access is not defined on each route, as it's inherited from parent
  children?: RouteDefinition[]
}

// Close all modals when navigating to a new route
router.beforeEach(() => {
  useModalStore().closeAll()
})

// redirect not found to 404
router.beforeEach((to, from, next) => {
  if (!to.matched.length) {
    next({ name: 'NotFoundPage' })
    return
  }
  next()
})

// catch errors and load ErrorPage
router.onError((error, to, from) => {
  errorTracker.track(error, { to: to.fullPath, from: from.fullPath, operation: 'router.onError' })
  router.push({ name: 'NotFoundPage' })
})

router.afterEach((to) => {
  setDocumentTitle(to.meta.title as string)
})

export default router
